import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useScrimmageSearch } from '@/hooks/useScrimmageSearch'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Loading from '@/components/common/Loading'
import ScrimmageFilters from '@/components/scrimmage/ScrimmageFilters'
import ScrimmageCard from '@/components/scrimmage/ScrimmageCard'
import PersonalizedHeader from '@/components/scrimmage/PersonalizedHeader'
import BidirectionalViewToggle from '@/components/scrimmage/BidirectionalViewToggle'
import { getRandomMarketingFeature } from '@/lib/hockeyData'
import CalendarUpload from '@/components/scrimmage/CalendarUpload'
import SaveSearchModal from '@/components/scrimmage/SaveSearchModal'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'

export default function ScrimmageBulletin() {
  const { sport = 'hockey' } = useParams()
  const { 
    results, 
    loading, 
    error, 
    filters, 
    updateFilter, 
    clearFilters,
    teamProfile,
    isPersonalized,
    profileLoaded,
    loadTeamProfile,
    applyProfileDefaults,
    togglePersonalized,
    calendarSyncStatus
  } = useScrimmageSearch(sport)
  
  // Random marketing feature on component mount
  const [marketingFeature] = useState(() => getRandomMarketingFeature())
  const [showCalendarUpload, setShowCalendarUpload] = useState(false)
  const [showSaveSearch, setShowSaveSearch] = useState(false)
  const [teamId, setTeamId] = useState(null)
  const [teamTier, setTeamTier] = useState(null)
  const [userEmail, setUserEmail] = useState('')
  const [postingTypeTab, setPostingTypeTab] = useState('all') // 'available', 'wanted', 'all'
  
  // Bidirectional view state
  const [matchPerspective, setMatchPerspective] = useState('demand')
  const [matchCounts, setMatchCounts] = useState({ demand_count: 0, supply_count: 0, excellent_count: 0 })
  
  // Derive precomputed view state from filters (single source of truth)
  const usePrecomputedView = filters.usePrecomputedMatches === true

  useEffect(() => {
    const loadTeam = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        setUserEmail('')
        setTeamId(null)
        setTeamTier(null)
        updateFilter('currentTeamId', null)
        loadTeamProfile(null)
        return
      }
      setUserEmail(session.user.email || '')
      const { data: team } = await supabase
        .from('teams')
        .select('id, season_tier')
        .eq('user_id', session.user.id)
        .single()
      if (team) {
        setTeamId(team.id)
        setTeamTier(team.season_tier || null)
        updateFilter('currentTeamId', team.id)
        // AUTO-ENABLE PRECOMPUTED MATCHES FOR LOGGED-IN USERS
        // This shows the sophisticated matchmaking algorithm results by default
        updateFilter('usePrecomputedMatches', true)
        updateFilter('matchPerspective', 'demand')
        // Load team profile for personalized view
        loadTeamProfile(team.id)
      } else {
        setTeamId(null)
        setTeamTier(null)
        updateFilter('currentTeamId', null)
        loadTeamProfile(null)
      }
    }
    loadTeam()
  }, [])

  // Apply profile defaults when profile is loaded for the first time
  useEffect(() => {
    if (profileLoaded && teamProfile && teamId && !isPersonalized) {
      applyProfileDefaults(teamProfile)
    }
  }, [profileLoaded, teamProfile, teamId])

  // Load match counts for bidirectional view when team is loaded
  useEffect(() => {
    const loadMatchCounts = async () => {
      if (!teamId) {
        setMatchCounts({ demand_count: 0, supply_count: 0, excellent_count: 0 })
        return
      }

      try {
        const { data, error } = await supabase.rpc('get_match_counts_for_team', {
          p_team_id: teamId,
          p_min_score: 50
        })

        if (error) {
          console.warn('Error loading match counts:', error)
          return
        }

        if (data && data.length > 0) {
          setMatchCounts(data[0])
        }
      } catch (err) {
        console.warn('Error loading match counts:', err)
      }
    }

    loadMatchCounts()
  }, [teamId])

  // REMOVED: Auto-fallback to standard search
  // We now ALWAYS use real-time scoring for logged-in users (like match-validation)
  // This ensures parity with the admin Match Validation page
  // The real-time scoring in useScrimmageSearch handles the case when there are no matches

  // Handle perspective change for bidirectional view
  const handlePerspectiveChange = (newPerspective) => {
    setMatchPerspective(newPerspective)
    // Force "all" tab when switching to precomputed mode to avoid filtering issues
    setPostingTypeTab('all')
    updateFilter('postingType', null)
    updateFilter('matchPerspective', newPerspective)
    updateFilter('usePrecomputedMatches', true)
  }

  // Switch back to standard view
  const handleStandardView = () => {
    updateFilter('usePrecomputedMatches', false)
  }

  const isProTeam = teamTier === 'paid'

  // Filter results by posting type tab
  // Skip filtering when in precomputed view (results are already filtered by the RPC)
  const filteredResults = usePrecomputedView 
    ? results 
    : results.filter(result => {
        if (postingTypeTab === 'all') return true
        return result.posting?.posting_type === postingTypeTab
      })

  return (
    <div className="min-h-screen bg-brand-cream">
      {/* Hero Section */}
      <div className="bg-brand-green text-white py-12">
        <Container>
          {/* Auth status */}
          <div className="flex justify-end mb-4 text-sm">
            {userEmail ? (
              <div className="flex items-center gap-3 opacity-90">
                <span>Signed in as <strong>{userEmail}</strong></span>
                <Link to="/dashboard" className="hover:underline">Dashboard</Link>
                <button
                  onClick={async () => { await supabase.auth.signOut(); window.location.reload() }}
                  className="hover:underline"
                >
                  Sign out
                </button>
              </div>
            ) : (
              <Link 
                to="/auth?tab=signin" 
                className="text-white hover:underline opacity-90 hover:opacity-100"
              >
                Team Login →
              </Link>
            )}
          </div>
          
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm font-medium mb-2 opacity-75">Bench Bulletin</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4 text-white">
              Find {sport.charAt(0).toUpperCase() + sport.slice(1)} Scrimmages
            </h1>
            <p className="text-lg md:text-xl mb-6 opacity-90">
              Connect with teams near you. Fill ice time. Build your schedule.
            </p>
            <div className="flex justify-center gap-4">
              {!userEmail && (
                <Link to="/auth?tab=signup">
                  <Button variant="secondary" className="bg-white text-brand-green hover:bg-gray-100">
                    Sign Up Free
                  </Button>
                </Link>
              )}
              {userEmail && (
                <Link to={teamId ? '/scrimmages/manage' : '/dashboard'}>
                  <Button variant="secondary" className="bg-white text-brand-green hover:bg-gray-100">
                    {teamId ? 'Manage Scrimmages' : 'View Dashboard'}
                  </Button>
                </Link>
              )}
              {!isProTeam && (
                <Link to="/pricing">
                  <Button variant="secondary" className="bg-white text-brand-green hover:bg-gray-100">
                    Pro Features
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </Container>
      </div>

      {/* Main Content */}
      <Container className="py-8">
        {/* Inline Calendar Upload for non-logged-in users or users without team */}
        {!teamId && (
          <div className="mb-6">
            <CalendarUpload
              mode="inline"
              onCalendarUploaded={(data) => {
                updateFilter('calendarEnabled', true)
                updateFilter('conflictMode', 'flexible')
                toast.success('Calendar applied! Filtering results...')
              }}
              matchCount={results.length}
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Filters */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-6">
              <ScrimmageFilters
                filters={filters}
                onFilterChange={updateFilter}
                onClearFilters={clearFilters}
                onUploadCalendar={() => setShowCalendarUpload(true)}
                teamId={teamId}
              />
              
              {/* Pro CTA with Rotating Features - Desktop only */}
              <Card className="hidden lg:block mt-4 bg-white border-2 border-brand-green overflow-hidden">
                <div className="bg-brand-green text-white px-4 py-3 -mx-6 -mt-6 mb-4">
                  <h3 className="font-heading text-lg font-bold text-white">
                    More Than Just Scrimmages
                  </h3>
                </div>
                <div className="mb-3 bg-brand-cream p-3 rounded-lg">
                  <p className="font-semibold text-brand-charcoal text-sm mb-1">{marketingFeature.title}</p>
                  <p className="text-xs text-brand-slate">{marketingFeature.desc}</p>
                </div>
                <p className="text-sm text-brand-charcoal mb-4">
                  Pro includes full team management tools to keep your season organized.
                </p>
                
                <div className="space-y-2">
                  <Link to="/auth?tab=signin">
                    <Button variant="outline" className="w-full">
                      Manage Your Postings
                    </Button>
                  </Link>
                  <Link to="/pricing" target="_blank" rel="noopener noreferrer">
                    <Button variant="primary" className="w-full">
                      See All Pro Features
                    </Button>
                  </Link>
                </div>
              </Card>
            </div>
          </div>

          {/* Main Area - Results */}
          <div className="lg:col-span-3">
            {/* Personalized Header for logged-in users with a team */}
            {teamProfile && (
              <PersonalizedHeader
                teamProfile={teamProfile}
                resultsCount={filteredResults.length}
                isPersonalized={isPersonalized}
                onTogglePersonalized={togglePersonalized}
                loading={loading}
              />
            )}

            {/* Bidirectional View Toggle for logged-in users with matches */}
            {teamId && (matchCounts.demand_count > 0 || matchCounts.supply_count > 0) && (
              <div className="mb-4">
                <div className="flex flex-wrap items-center gap-3">
                  <BidirectionalViewToggle
                    perspective={matchPerspective}
                    onPerspectiveChange={handlePerspectiveChange}
                    demandCount={matchCounts.demand_count}
                    supplyCount={matchCounts.supply_count}
                    excellentCount={matchCounts.excellent_count}
                  />
                  {usePrecomputedView && (
                    <button
                      onClick={handleStandardView}
                      className="text-sm text-gray-500 hover:text-gray-700 underline"
                    >
                      Back to standard view
                    </button>
                  )}
                </div>
                {usePrecomputedView && (
                  <p className="text-xs text-brand-slate mt-2">
                    {matchPerspective === 'demand' 
                      ? 'Scrimmage Opportunities: Teams with ice available that match your level and schedule'
                      : 'Home Opportunities: Teams who might want to scrimmage at your rink'
                    }
                  </p>
                )}
              </div>
            )}

            {/* Posting Type Tabs - hidden when in precomputed view */}
            {!usePrecomputedView && (
              <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-gray-200 pb-3">
                <button
                  onClick={() => {
                    setPostingTypeTab('available')
                    updateFilter('postingType', 'available')
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    postingTypeTab === 'available'
                      ? 'bg-brand-green text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>🏒</span>
                  <span>Available Ice</span>
                  {!loading && postingTypeTab === 'available' && (
                    <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs">{filteredResults.length}</span>
                  )}
                </button>
                <button
                  onClick={() => {
                    setPostingTypeTab('wanted')
                    updateFilter('postingType', 'wanted')
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                    postingTypeTab === 'wanted'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>🔍</span>
                  <span>Looking For</span>
                  {!loading && postingTypeTab === 'wanted' && (
                    <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs">{filteredResults.length}</span>
                  )}
                </button>
                <button
                  onClick={() => {
                    setPostingTypeTab('all')
                    updateFilter('postingType', null)
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    postingTypeTab === 'all'
                      ? 'bg-gray-800 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All
                  {!loading && postingTypeTab === 'all' && (
                    <span className="ml-1 bg-white/20 px-1.5 py-0.5 rounded text-xs">{filteredResults.length}</span>
                  )}
                </button>

                <div className="flex-1"></div>

                {/* Results count and save search */}
                <div className="flex items-center gap-3">
                  {!loading && (
                    <span className="text-brand-slate text-sm">
                      {filteredResults.length} {filteredResults.length === 1 ? 'result' : 'results'}
                    </span>
                  )}
                  {teamId && (
                    <Button variant="outline" size="sm" onClick={() => setShowSaveSearch(true)}>
                      ⭐ Save Search
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Results count for precomputed view */}
            {usePrecomputedView && (
              <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3">
                <span className="text-brand-slate text-sm">
                  {filteredResults.length} {filteredResults.length === 1 ? 'match' : 'matches'} found
                </span>
                {teamId && (
                  <Button variant="outline" size="sm" onClick={() => setShowSaveSearch(true)}>
                    ⭐ Save Search
                  </Button>
                )}
              </div>
            )}

            {/* Results Header */}
            <div className="mb-4">
              <h2 className="font-heading text-2xl font-bold text-brand-charcoal">
                {usePrecomputedView 
                  ? (matchPerspective === 'demand' ? 'Scrimmage Opportunities' : 'Teams Interested in Your Ice')
                  : (
                    <>
                      {postingTypeTab === 'available' && 'Teams with Available Ice'}
                      {postingTypeTab === 'wanted' && 'Teams Looking for Scrimmages'}
                      {postingTypeTab === 'all' && (isPersonalized && teamProfile ? 'Matching Scrimmages' : 'All Scrimmages')}
                    </>
                  )
                }
              </h2>
              <p className="text-sm text-brand-slate mt-1">
                {usePrecomputedView
                  ? (matchPerspective === 'demand' 
                      ? 'Teams with solo ice that could be available for a scrimmage'
                      : 'Teams at your level who might be interested in your available ice'
                    )
                  : (
                    <>
                      {postingTypeTab === 'available' && 'Teams offering their ice time for a scrimmage'}
                      {postingTypeTab === 'wanted' && 'Teams seeking a scrimmage partner'}
                      {postingTypeTab === 'all' && 'Browse all available scrimmage opportunities'}
                    </>
                  )
                }
              </p>
              {usePrecomputedView && matchPerspective === 'demand' && (
                <>
                  {calendarSyncStatus?.warning ? (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded">
                      <p className="text-xs text-amber-700 flex items-start gap-1">
                        <span className="mt-0.5">⚠️</span>
                        <span>{calendarSyncStatus.warning}</span>
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        Schedule scores may show matches as "excellent" even if you have conflicts.
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                      <span>✅</span>
                      <span>
                        Real-time scoring using your current calendar
                        {calendarSyncStatus?.eventCount > 0 && ` (${calendarSyncStatus.eventCount} events synced)`}. 
                        Sorted by best fit, then nearest date.
                      </span>
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Loading State */}
            {loading && (
              <Card>
                <div className="text-center py-12">
                  <Loading text="Searching scrimmages..." />
                </div>
              </Card>
            )}

            {/* Error State */}
            {error && !loading && (
              <Card>
                <div className="text-center py-12">
                  <p className="text-red-600 mb-4">
                    Failed to load scrimmages: {error}
                  </p>
                  <Button variant="secondary" onClick={() => window.location.reload()}>
                    Try Again
                  </Button>
                </div>
              </Card>
            )}

            {/* Results */}
            {!loading && !error && (
              <>
                {filteredResults.length === 0 ? (
                  <Card>
                    <div className="text-center py-12">
                      <h3 className="font-heading text-xl font-bold text-brand-charcoal mb-2">
                        {postingTypeTab === 'available' && 'No available ice found'}
                        {postingTypeTab === 'wanted' && 'No teams looking for scrimmages'}
                        {postingTypeTab === 'all' && 'No scrimmages found'}
                      </h3>
                      <p className="text-brand-slate mb-6">
                        {postingTypeTab === 'available' && 'Try adjusting your filters or check back later.'}
                        {postingTypeTab === 'wanted' && 'No teams are currently looking for scrimmages matching your filters.'}
                        {postingTypeTab === 'all' && 'Try adjusting your filters or be the first to post a scrimmage!'}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        {postingTypeTab !== 'all' && (
                          <Button variant="outline" onClick={() => { setPostingTypeTab('all'); updateFilter('postingType', null); }}>
                            View All Scrimmages
                          </Button>
                        )}
                        {teamId ? (
                          <Link to="/scrimmages/manage">
                            <Button variant="primary">
                              Post a Scrimmage
                            </Button>
                          </Link>
                        ) : (
                          <Link to={userEmail ? '/team-setup' : '/auth?tab=signup'}>
                            <Button variant="primary">
                              Post a Scrimmage
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {filteredResults.map((result, index) => (
                      <ScrimmageCard 
                        key={`${result.posting.id}-${result.team_info?.name || index}`} 
                        result={result} 
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Info Card */}
            {!loading && filteredResults.length > 0 && (
              <Card className="mt-6 bg-blue-50 border-blue-200">
                <h3 className="font-heading text-lg font-bold text-brand-charcoal mb-2">
                  How it works
                </h3>
                <ul className="text-sm text-brand-slate space-y-1">
                  <li>• Browse available scrimmages from teams across the platform</li>
                  <li>• Filter by location, level, date, and preferences</li>
                  <li>• Contact teams directly to arrange scrimmage details</li>
                  <li>• Post up to 3 active scrimmages (unlimited with Pro)</li>
                </ul>
              </Card>
            )}

            {/* Pro CTA - Mobile only, at bottom */}
            <Card className="lg:hidden mt-6 bg-white border border-brand-green overflow-hidden">
              <div className="bg-brand-green text-white px-4 py-2 -mx-6 -mt-6 mb-3">
                <h3 className="font-heading text-base font-bold text-white">
                  More Than Just Scrimmages
                </h3>
              </div>
              <div className="mb-3 bg-brand-cream p-2 rounded-lg">
                <p className="font-semibold text-brand-charcoal text-xs mb-1">{marketingFeature.title}</p>
                <p className="text-xs text-brand-slate">{marketingFeature.desc}</p>
              </div>
              <p className="text-xs text-brand-charcoal mb-3">
                Pro includes full team management tools to keep your season organized.
              </p>
              
              <div className="space-y-2">
                <Link to="/auth?tab=signin">
                  <Button variant="outline" size="sm" className="w-full">
                    Manage Your Postings
                  </Button>
                </Link>
                <Link to="/pricing" target="_blank" rel="noopener noreferrer">
                  <Button variant="primary" size="sm" className="w-full">
                    See All Pro Features
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </Container>

      {/* Public Calendar Upload Modal */}
      {showCalendarUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl bg-white rounded-lg p-4 shadow-xl">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-lg font-bold text-brand-charcoal">Upload Calendar</h3>
              <button onClick={() => setShowCalendarUpload(false)} className="text-sm text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <CalendarUpload
              mode="public"
              onCalendarUploaded={() => setShowCalendarUpload(false)}
              onCancel={() => setShowCalendarUpload(false)}
            />
          </div>
        </div>
      )}

      {/* Save Search Modal */}
      {showSaveSearch && teamId && (
        <SaveSearchModal
          teamId={teamId}
          onSave={() => setShowSaveSearch(false)}
          onCancel={() => setShowSaveSearch(false)}
        />
      )}
    </div>
  )
}

